import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, cartApi, checkoutApi } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { PriceDisplay } from '@/components/PriceDisplay';
import { withClickSpan } from '@/lib/clickSpan';
import { toast } from '@/components/ui/sonner';

export function Checkout() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const qc = useQueryClient();

  // Bounce anon users to login (preserve intent).
  useEffect(() => {
    if (me.isLoading) return;
    if (!me.data) navigate('/login?next=/checkout', { replace: true });
  }, [me.isLoading, me.data, navigate]);

  // /preview requires the cart line items in the body (the checkout service
  // doesn't read the cart on its own — that's a separate service in v2).
  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: async () => (await cartApi.get()).data,
    enabled: !!me.data,
  });

  const cartItems = cart.data?.items ?? [];
  const cartKey = cartItems.map((i) => `${i.productId}:${i.quantity}`).join(',');

  const preview = useQuery({
    queryKey: ['checkout-preview', cartKey],
    queryFn: async () =>
      (
        await checkoutApi.preview(
          cartItems.map((i) => ({ productId: i.productId, quantity: i.quantity })),
        )
      ).data,
    enabled: !!me.data && cartItems.length > 0,
  });

  const placeMut = useMutation({
    mutationFn: () => checkoutApi.place(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['cart'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Order placed!');
      navigate(`/orders/${encodeURIComponent(res.data.id)}`);
    },
    onError: (err: Error & { traceId?: string | null }) => {
      // The session can expire between page load and clicking Place
      // Order. checkout now 401s that case (it used to 500/400) — send
      // the visitor to log back in rather than showing a generic "Order
      // failed" toast, which reads like a payment/system failure.
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login?next=/checkout', { replace: true });
        return;
      }
      toast.error('Order failed', { description: err.traceId ?? err.message });
    },
  });

  if (me.isLoading || !me.data) return <Skeleton className="h-64" />;
  if (preview.isLoading) return <Skeleton className="h-64" />;
  if (preview.error) {
    return <p className="text-destructive">Could not load checkout preview.</p>;
  }

  const items = preview.data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <h1 className="font-display text-3xl mb-3">Nothing to check out</h1>
        <p className="text-(--color-ink-muted)">Your cart is empty.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="font-display text-3xl">Checkout</h1>
      <p className="text-(--color-ink-muted)">Signed in as {me.data.email}.</p>

      <ul className="divide-y rounded-lg border bg-card">
        {items.map((it) => (
          <li key={it.productId} className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="font-medium">{it.name}</p>
              <p className="text-sm text-(--color-ink-muted)">×{it.quantity}</p>
            </div>
            <PriceDisplay knuts={it.unit_price_knuts * it.quantity} />
          </li>
        ))}
      </ul>

      <Separator />
      <div className="flex items-center justify-between">
        <span className="font-display text-xl">Total</span>
        <PriceDisplay knuts={preview.data?.total_knuts ?? 0} className="text-2xl" />
      </div>

      <Button
        type="button"
        variant="cta"
        size="lg"
        className="w-full"
        disabled={placeMut.isPending}
        onClick={() =>
          // placeMut's onError above already owns the UX (toast, or a
          // redirect to login on a 401) — the trailing .catch only
          // prevents an unhandled promise rejection for a failure that
          // has already been handled.
          withClickSpan('checkout-place', { 'app.checkout.item_count': items.length }, () =>
            placeMut.mutateAsync(),
          ).catch(() => {})
        }
      >
        {placeMut.isPending ? 'Placing order…' : 'Place Order'}
      </Button>
    </div>
  );
}
